import { CoopCancelReasonType } from '@app/DashboardComponents/CoopCancelAppointmentComponent';
import errorReporter from '@app/errorReporter';
import { cancelTour } from 'api/TourController';
import { Icon, InputTextArea, message, Modal, Select, Text } from 'components';
import React from 'react';
import styled from 'styled-components';
import { makeModalResponsive, ModalType } from 'utils';
import { zPositions } from '@/constants';
import { TourCancelType } from '../types';

const TIME_SUGGESTION_TYPES = [
  TourCancelType.SUGGESTION_ACCEPT,
  TourCancelType.SUGGESTION_DENY,
  TourCancelType.SUGGESTION_CANCEL,
  TourCancelType.SUGGESTION_CANCEL_LAST,
];

const WithSubmitted = ({
  setReason,
  setDetails,
}: {
  setReason: (reason: CoopCancelReasonType) => void;
  setDetails: (detail: string) => void;
}) => {
  const [isOtherReason, setIsOtherReason] = React.useState(false);
  return (
    <>
      <Text type="secondary" block>
        All showing requests will be cancelled and notifications will be sent.
      </Text>
      <StyledReasonText block>Reason for cancelling</StyledReasonText>
      <Select
        zIndex={6000}
        defaultValue={{ label: 'I will reschedule', value: 'reschedule' }}
        options={[
          { label: 'I will reschedule', value: 'reschedule' },
          { label: 'Client is no longer interested', value: 'notInterested' },
          { label: 'Other (Custom)', value: 'other' },
        ]}
        labelInValue
        onChange={selected => {
          setReason(selected.value);
          setIsOtherReason(selected.value === 'other');
          setDetails(selected.value === 'other' ? '' : selected.label);
        }}
        data-cy="tours-select-cancel-reason"
      />
      {isOtherReason && (
        <StyledTextArea
          placeholder={'Why are you cancelling this tour?'}
          onChange={e => setDetails(e.target.value)}
          data-cy="tours-cancel-reason-details"
        />
      )}
    </>
  );
};

const showConfirmDeleteTourModal = (
  tourId: string,
  cancelType = TourCancelType.LAST_ITEM,
  details?: string,
  reason?: CoopCancelReasonType,
  onConfirm?: () => Promise<void>,
) => {
  const modal = Modal.confirm({
    zIndex: zPositions.tourModalSubModal,
    centered: true,
    autoFocusButton: null,
  });

  const reasonAndDetails = {
    reason: 'reschedule' as CoopCancelReasonType,
    details: 'I will reschedule',
  };

  const setReason = (cancelReason: CoopCancelReasonType) => {
    reasonAndDetails.reason = cancelReason;
  };

  const setDetails = (cancelDetails: string) => {
    reasonAndDetails.details = cancelDetails;
    modal.update((prevConfig: any) => ({
      ...prevConfig,
      okButtonProps: {
        ...prevConfig.okButtonProps,
        disabled: cancelDetails === '',
      },
    }));
  };

  modal.update({
    title: {
      [TourCancelType.LAST_ITEM]: 'Are you sure you want to delete this tour?',
      [TourCancelType.WITH_SUBMITTED_SHOWING]:
        'Are you sure you want to cancel the tour and showing requests?',
      [TourCancelType.WITHOUT_SUBMITTED_SHOWING]:
        'Are you sure you want to cancel this tour?',
      [TourCancelType.SUGGESTION_ACCEPT]:
        'Accepting this showing will change the date of the tour',
      [TourCancelType.SUGGESTION_DENY]:
        'Are you sure you want to deny this new time suggestion?',
      [TourCancelType.SUGGESTION_CANCEL]:
        'Are you sure you want to deny this new time suggestion?',
      [TourCancelType.SUGGESTION_CANCEL_LAST]:
        'Denying this showing will delete the tour',
    }[cancelType],
    icon: (
      <Icon
        width={22}
        height={22}
        color="warningColor"
        type={'antdExclamationOutlined'}
      />
    ),
    content: {
      [TourCancelType.LAST_ITEM]: (
        <Text type="secondary">
          Removing the last stop in a tour will delete the tour.
        </Text>
      ),
      [TourCancelType.WITHOUT_SUBMITTED_SHOWING]: (
        <Text type="secondary">
          The tour and the draft showings will be deleted forever.
        </Text>
      ),
      [TourCancelType.WITH_SUBMITTED_SHOWING]: (
        <WithSubmitted setReason={setReason} setDetails={setDetails} />
      ),
      [TourCancelType.SUGGESTION_ACCEPT]: (
        <Text type="secondary">
          The suggested time is on a different date from this tour. Since this
          is the only stop on this tour, the tour date will be changed.
        </Text>
      ),
      [TourCancelType.SUGGESTION_DENY]: (
        <Text type="secondary">
          If denied, the original showing request will be kept.
        </Text>
      ),
      [TourCancelType.SUGGESTION_CANCEL]: (
        <Text type="secondary">
          If denied, the original showing request will be cancelled.
        </Text>
      ),
      [TourCancelType.SUGGESTION_CANCEL_LAST]: (
        <Text type="secondary">
          If you deny this suggested time change, the original showing request
          will be cancelled. The tour will be deleted since there are no other
          showings in this tour.
        </Text>
      ),
    }[cancelType],
    onOk() {
      return (async () => {
        try {
          if (onConfirm) {
            await onConfirm();
          }

          let cancelled = false;
          if (
            [
              TourCancelType.SUGGESTION_CANCEL_LAST,
              TourCancelType.WITH_SUBMITTED_SHOWING,
            ].includes(cancelType)
          ) {
            await cancelTour(
              tourId,
              reasonAndDetails.details,
              reasonAndDetails.reason,
            );
            cancelled = true;
          } else if (
            [
              TourCancelType.LAST_ITEM,
              TourCancelType.WITHOUT_SUBMITTED_SHOWING,
            ].includes(cancelType)
          ) {
            await cancelTour(tourId, details, reason);
            cancelled = true;
          }

          if (cancelled) {
            window.location.href = `/#/my_business/showings/buyer`;
            const successMsg =
              TourCancelType.SUGGESTION_CANCEL_LAST === cancelType
                ? 'Suggested time denied. The tour has been deleted.'
                : 'Tour has been deleted.';
            message.success(successMsg);
          }
        } catch (err) {
          const errorMsg = TIME_SUGGESTION_TYPES.includes(cancelType)
            ? 'Error processing suggested time change'
            : 'Error cancelling the tour';
          message.error(errorMsg);
          errorReporter.error(err);
        }
      })();
    },
    okText: {
      [TourCancelType.LAST_ITEM]: 'Delete Tour',
      [TourCancelType.WITH_SUBMITTED_SHOWING]: 'Cancel Tour',
      [TourCancelType.WITHOUT_SUBMITTED_SHOWING]: 'Cancel Tour',
      [TourCancelType.SUGGESTION_ACCEPT]: 'Accept Showing',
      [TourCancelType.SUGGESTION_DENY]: 'Deny Showing',
      [TourCancelType.SUGGESTION_CANCEL]: 'Cancel Showing',
      [TourCancelType.SUGGESTION_CANCEL_LAST]: 'Deny Showing',
    }[cancelType],
    cancelText: TIME_SUGGESTION_TYPES.includes(cancelType) ? 'Back' : 'Go Back',
    getContainer: document.getElementById('tour-edit-page-root'),
  });

  makeModalResponsive(modal, {
    modalType: ModalType.CONFIRM,
    dataCys: {
      ok: 'cancel-tour-button',
      cancel: 'go-back-button',
      content: 'confirm-delete-tour-modal',
    },
  });
};

export default showConfirmDeleteTourModal;

const StyledReasonText = styled(Text)`
  margin: 8px 0px;
`;

const StyledTextArea = styled(InputTextArea)`
  margin: 8px 0px;
`;
